export type NotificationChannel = "email" | "sms" | "both";
export type NotificationFrequency = "daily" | "weekly" | "monthly";

export interface NotificationPreferences {
  enabled: boolean;
  channel: NotificationChannel;
  frequency: NotificationFrequency;
  categories: Record<NotificationCategoryId, boolean>;
}

export interface ReferralIdentity {
  userId?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  profileId?: string | null;
  targetArea?: string | null;
}

export const NOTIFICATION_STORAGE_KEY =
  "sill-dashboard-notification-preferences";

export const NOTIFICATION_CATEGORIES = [
  {
    id: "rate_changes",
    label: "Rate change alerts",
    description: "Payment-impacting mortgage rate movement.",
  },
  {
    id: "readiness_updates",
    label: "Readiness score updates",
    description: "Meaningful changes to your score or coaching status.",
  },
  {
    id: "milestone_reminders",
    label: "Milestone reminders",
    description: "Nudges when the next dashboard milestone needs attention.",
  },
  {
    id: "weekly_digest",
    label: "Weekly digest",
    description:
      "A bundled summary of your dashboard progress and market changes.",
  },
  {
    id: "lease_expiration_countdown",
    label: "Lease expiration countdown",
    description: "Countdown reminders tied to your move-out timing.",
  },
] as const;

export type NotificationCategoryId =
  (typeof NOTIFICATION_CATEGORIES)[number]["id"];

export const NOTIFICATION_CHANNEL_OPTIONS: Array<{
  description: string;
  label: string;
  value: NotificationChannel;
}> = [
  {
    value: "email",
    label: "Email",
    description: "Delivery to your inbox.",
  },
  {
    value: "sms",
    label: "SMS",
    description: "Text alerts only.",
  },
  {
    value: "both",
    label: "Both",
    description: "Email and text messages.",
  },
];

export const NOTIFICATION_FREQUENCY_OPTIONS: Array<{
  description: string;
  label: string;
  value: NotificationFrequency;
}> = [
  {
    value: "daily",
    label: "Daily",
    description: "A once-a-day summary for this notification type.",
  },
  {
    value: "weekly",
    label: "Weekly",
    description: "A weekly reminder cadence.",
  },
  {
    value: "monthly",
    label: "Monthly",
    description: "A lighter monthly cadence.",
  },
];

export function getDefaultNotificationPreferences(): NotificationPreferences {
  return {
    enabled: true,
    channel: "both",
    frequency: "daily",
    categories: NOTIFICATION_CATEGORIES.reduce(
      (accumulator, category) => {
        accumulator[category.id] = true;
        return accumulator;
      },
      {} as Record<NotificationCategoryId, boolean>,
    ),
  };
}

export function normalizeNotificationPreferences(
  value: unknown,
): NotificationPreferences {
  const defaults = getDefaultNotificationPreferences();

  if (!isRecord(value)) {
    return defaults;
  }

  const categories = isRecord(value.categories) ? value.categories : {};

  return {
    enabled:
      typeof value.enabled === "boolean" ? value.enabled : defaults.enabled,
    channel: isNotificationChannel(value.channel)
      ? value.channel
      : defaults.channel,
    frequency: isNotificationFrequency(value.frequency)
      ? value.frequency
      : defaults.frequency,
    categories: NOTIFICATION_CATEGORIES.reduce(
      (accumulator, category) => {
        const categoryValue = categories[category.id];
        accumulator[category.id] =
          typeof categoryValue === "boolean"
            ? categoryValue
            : defaults.categories[category.id];
        return accumulator;
      },
      {} as Record<NotificationCategoryId, boolean>,
    ),
  };
}

export function loadNotificationPreferences(
  storageKey = NOTIFICATION_STORAGE_KEY,
): NotificationPreferences | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }

    return normalizeNotificationPreferences(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function saveNotificationPreferences(
  preferences: NotificationPreferences,
  storageKey = NOTIFICATION_STORAGE_KEY,
) {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(
    storageKey,
    JSON.stringify(normalizeNotificationPreferences(preferences)),
  );
}

export function clearNotificationPreferences(
  storageKey = NOTIFICATION_STORAGE_KEY,
) {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.removeItem(storageKey);
}

export function updateNotificationPreferenceCategory(
  preferences: NotificationPreferences,
  categoryId: NotificationCategoryId,
  enabled: boolean,
): NotificationPreferences {
  return {
    ...preferences,
    categories: {
      ...preferences.categories,
      [categoryId]: enabled,
    },
  };
}

export function setNotificationChannel(
  preferences: NotificationPreferences,
  channel: NotificationChannel,
): NotificationPreferences {
  return {
    ...preferences,
    channel,
  };
}

export function setNotificationFrequency(
  preferences: NotificationPreferences,
  frequency: NotificationFrequency,
): NotificationPreferences {
  return {
    ...preferences,
    frequency,
  };
}

export function disableNotificationPreferences(
  preferences: NotificationPreferences,
): NotificationPreferences {
  return {
    ...preferences,
    enabled: false,
  };
}

export function getNotificationStopCopy() {
  return "SMS alerts can be stopped at any time by replying STOP. Email updates include an unsubscribe link in the footer.";
}

export function getNotificationOptOutCopy() {
  return "Turn off all categories below or pause notifications entirely.";
}

export function getReferralDisplayName(identity: ReferralIdentity) {
  const firstName = normalizeOptionalText(identity.firstName);
  const lastName = normalizeOptionalText(identity.lastName);

  if (firstName || lastName) {
    return [firstName, lastName].filter(Boolean).join(" ");
  }

  const email = normalizeOptionalText(identity.email);
  if (email) {
    const localPart = email.split("@")[0] ?? email;
    return toTitleCase(slugToWords(localPart));
  }

  return "Your referral";
}

export function getReferralShareSubject(identity: ReferralIdentity) {
  return `${getReferralDisplayName(identity)} invited you to Sill`;
}

export function getReferralShareMessage(
  identity: ReferralIdentity,
  referralLink: string,
) {
  return `${getReferralDisplayName(identity)} invited you to explore Sill. Join through this link: ${referralLink}`;
}

export function buildSmsHref(message: string) {
  return `sms:?body=${encodeURIComponent(message)}`;
}

export function buildMailtoHref(subject: string, body: string) {
  const params = new URLSearchParams({
    body,
    subject,
  });

  return `mailto:?${params.toString()}`;
}

function isNotificationChannel(value: unknown): value is NotificationChannel {
  return value === "email" || value === "sms" || value === "both";
}

function isNotificationFrequency(
  value: unknown,
): value is NotificationFrequency {
  return value === "daily" || value === "weekly" || value === "monthly";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function normalizeOptionalText(value: string | null | undefined) {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function slugToWords(value: string) {
  return value
    .replace(/[-_.]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function toTitleCase(value: string) {
  return value
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
